import React, {useMemo, useRef, useEffect} from 'react';
import CuratedEventContent from './CuratedEventContent';
import {useInfiniteGlobalSearchWithParamsAndLocation} from '../../../hooks/useFetchGlobalSearch';
import {useInfiniteMeetups} from '../../../hooks/useMeetUps';
import useGetUserLocation from '../../../hooks/useUserLocation';
import {useOffers} from '../../../hooks/useOffers';
import {calculateDistance, convertFootToMiles} from '../../../utils/distance';
import {SpinnerLoader} from '../../../components/Loader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {Event} from '../../../types/article';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ComicConContentProps {
  eventIds?: number[];
  event?: Event;
}

const ComicConContent: React.FC<ComicConContentProps> = ({eventIds = [], event}) => {
  const userLocation = useGetUserLocation();
  const hasEverLoadedData = useRef(false);
  const [accumulatedOffers, setAccumulatedOffers] = React.useState<any[]>([]);
  const [accumulatedMeetups, setAccumulatedMeetups] = React.useState<any[]>([]);
  const [accumulatedEvents, setAccumulatedEvents] = React.useState<any[]>([]);
  const [accumulatedNoBadgeEvents, setAccumulatedNoBadgeEvents] = React.useState<any[]>([]);

  // Target location for San Diego Convention Center
  const targetLocation = {
    latitude: 32.7065,
    longitude: -117.1613,
  };

  const comicConTopicId = useMemo(() => {
    // @ts-ignore - process.env is defined at build time
    return process.env.NODE_ENV === 'production' ? '308' : '220';
  }, []);

  // Topic ID for "no badge required" activations (this should be a separate tag)
  const noBadgeTopicId = useMemo(() => {
    // @ts-ignore - process.env is defined at build time
    return process.env.NODE_ENV === 'production' ? '309' : '221';
  }, []);
  // Search params for offers, events, no-badge events, and places
  const offersSearchParams = useMemo(
    () => ({
      type: 'offer',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      term: '',
      orderBy: 'ends_at',
      sort: {by: 'ends_at', direction: 'asc'},
    }),
    [comicConTopicId]
  );

  const eventSearchParams = useMemo(
    () => ({
      type: 'event',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      'topics[]': comicConTopicId, // Search for Comic Con events only
      term: '',
      orderBy: 'distance',
      sort: {by: 'start_date_time', direction: 'asc'},
    }),
    [comicConTopicId]
  );

  const noBadgeEventSearchParams = useMemo(
    () => ({
      type: 'event',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      topic: noBadgeTopicId,
      term: '',
      orderBy: 'default',
      sort: 'distance',
    }),
    [noBadgeTopicId]
  );

  const placesSearchParams = useMemo(
    () => ({
      type: 'place',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      topic: comicConTopicId,
      term: '',
      orderBy: 'default',
      sort: 'distance',
    }),
    [comicConTopicId]
  );

  // Data fetching hooks
  const fetchComicConOffers = useInfiniteGlobalSearchWithParamsAndLocation(offersSearchParams);
  const fetchComicConEvents = useInfiniteGlobalSearchWithParamsAndLocation(eventSearchParams);
  const fetchNoBadgeEvents = useInfiniteGlobalSearchWithParamsAndLocation(noBadgeEventSearchParams);
  const fetchComicConPlaces = useInfiniteGlobalSearchWithParamsAndLocation(placesSearchParams);
  const fetchMeetups = useInfiniteMeetups();

  // Fetch offers for the current event
  const {offers: eventOffers, offersIsLoading} = useOffers({
    articleType: 'eventId',
    articleId: event?.id?.toString(),
    enabled: !!event?.id,
  });

  // Auto-fetch all pages for events until all are loaded
  useEffect(() => {
    if (
      fetchComicConEvents.hasNextPage &&
      !fetchComicConEvents.isFetchingNextPage &&
      !fetchComicConEvents.isLoading
    ) {
      fetchComicConEvents.fetchNextPage();
    }
  }, [
    fetchComicConEvents.hasNextPage,
    fetchComicConEvents.isFetchingNextPage,
    fetchComicConEvents.isLoading,
    fetchComicConEvents.fetchNextPage,
  ]);

  // Accumulate Comic-Con events in state as new pages are loaded
  useEffect(() => {
    if (!fetchComicConEvents.data?.pages) return;
    const allEvents = fetchComicConEvents.data.pages.flatMap((page: any) => page.data.data || []);
    setAccumulatedEvents((prev) => {
      if (prev.length === allEvents.length) return prev;
      return allEvents;
    });
  }, [fetchComicConEvents.data?.pages]);

  // Accumulate no-badge events in state as new pages are loaded
  useEffect(() => {
    if (!fetchNoBadgeEvents.data?.pages) return;
    const allNoBadgeEvents = fetchNoBadgeEvents.data.pages.flatMap(
      (page: any) => page.data.data || []
    );
    setAccumulatedNoBadgeEvents((prev) => {
      if (prev.length === allNoBadgeEvents.length) return prev;
      return allNoBadgeEvents;
    });
  }, [fetchNoBadgeEvents.data?.pages]);

  // Auto-fetch all pages for meetups when the first page loads
  useEffect(() => {
    if (fetchMeetups.data && fetchMeetups.hasNextPage && !fetchMeetups.isFetchingNextPage) {
      fetchMeetups.fetchNextPage();
    }
  }, [fetchMeetups.data, fetchMeetups.hasNextPage, fetchMeetups.isFetchingNextPage, fetchMeetups]);

  // Accumulate meetups in state as new pages are loaded
  useEffect(() => {
    if (!fetchMeetups.data?.pages) return;
    const allMeetups = fetchMeetups.data.pages.flatMap((page: any) => page.data.data || []);
    setAccumulatedMeetups((prev) => {
      if (prev.length === allMeetups.length) return prev;
      return allMeetups;
    });
  }, [fetchMeetups.data?.pages]);

  // Accumulate offers as pages load (unchanged)
  useEffect(() => {
    if (fetchComicConOffers.data) {
      const allOffers = fetchComicConOffers.data.pages.flatMap((page: any) => page.data.data) || [];
      setAccumulatedOffers(allOffers);
    }
  }, [fetchComicConOffers.data]);

  // Check if all required parameters are valid
  const areParamsValid = useMemo(() => {
    return (
      offersSearchParams.type &&
      offersSearchParams.lat &&
      offersSearchParams.long &&
      eventSearchParams.type &&
      eventSearchParams.lat &&
      eventSearchParams.long &&
      noBadgeEventSearchParams.type &&
      noBadgeEventSearchParams.lat &&
      noBadgeEventSearchParams.long &&
      placesSearchParams.type &&
      placesSearchParams.lat &&
      placesSearchParams.long &&
      comicConTopicId &&
      noBadgeTopicId
    );
  }, [
    offersSearchParams,
    eventSearchParams,
    noBadgeEventSearchParams,
    placesSearchParams,
    comicConTopicId,
    noBadgeTopicId,
  ]);

  // Check if all data is loaded and parameters are valid
  const isDataLoaded = useMemo(() => {
    if (!areParamsValid) {
      return false;
    }
    const allFetchesComplete =
      !fetchComicConOffers.isLoading &&
      !fetchComicConEvents.isLoading &&
      !fetchNoBadgeEvents.isLoading &&
      !fetchComicConPlaces.isLoading &&
      !fetchMeetups.isLoading &&
      (!event?.id || !offersIsLoading);
    if (allFetchesComplete) {
      hasEverLoadedData.current = true;
    }
    return allFetchesComplete;
  }, [
    areParamsValid,
    fetchComicConOffers.isLoading,
    fetchComicConEvents.isLoading,
    fetchNoBadgeEvents.isLoading,
    fetchComicConPlaces.isLoading,
    fetchMeetups.isLoading,
    offersIsLoading,
    event?.id,
  ]);

  // Check if this is initial loading (no data yet and currently loading)
  const isInitialLoading = useMemo(() => {
    if (!areParamsValid) {
      return false;
    }
    if (hasEverLoadedData.current) {
      return false;
    }
    const hasAnyData =
      fetchComicConOffers.data ||
      fetchComicConEvents.data ||
      fetchNoBadgeEvents.data ||
      fetchComicConPlaces.data ||
      fetchMeetups.data;
    if (hasAnyData && isDataLoaded) {
      hasEverLoadedData.current = true;
      return false;
    }
    if (hasAnyData && !isDataLoaded) {
      return true;
    }
    const isAnyLoading =
      fetchComicConOffers.isLoading ||
      fetchComicConEvents.isLoading ||
      fetchNoBadgeEvents.isLoading ||
      fetchComicConPlaces.isLoading ||
      fetchMeetups.isLoading ||
      offersIsLoading;
    return isAnyLoading && !hasEverLoadedData.current;
  }, [
    areParamsValid,
    fetchComicConOffers.data,
    fetchComicConEvents.data,
    fetchComicConPlaces.data,
    fetchMeetups.data,
    fetchComicConOffers.isLoading,
    fetchComicConEvents.isLoading,
    fetchComicConPlaces.isLoading,
    fetchMeetups.isLoading,
    offersIsLoading,
  ]);

  // Custom data loader for Comic-Con (memoized, like FarmersMarketContent)
  const customDataLoader = useMemo(() => {
    return {
      loadOffers: () => {
        if (!isDataLoaded) return [];
        const mergedOffers = [...accumulatedOffers];
        if (eventOffers && eventOffers.length > 0) {
          mergedOffers.push(...eventOffers);
        }
        const comicConStartDate = dayjs('2025-07-24').utc().startOf('day');
        const comicConEndDate = dayjs('2025-07-27').utc().endOf('day');
        const filteredOffers = mergedOffers.filter((offer) => {
          const offerStartDate = dayjs.utc(offer.start_date_time || offer.starts_at);
          const offerEndDate = dayjs.utc(offer.end_date_time || offer.ends_at);
          const isWithinDateRange =
            offerStartDate.isBetween(comicConStartDate, comicConEndDate, null, '[]') ||
            offerEndDate.isBetween(comicConStartDate, comicConEndDate, null, '[]') ||
            (offerStartDate.isBefore(comicConStartDate) && offerEndDate.isAfter(comicConEndDate));
          if (!isWithinDateRange) return false;
          let offerLatitude, offerLongitude;
          if (offer.place?.latitude && offer.place?.longitude) {
            offerLatitude = offer.place.latitude;
            offerLongitude = offer.place.longitude;
          } else if (offer.event?.latitude && offer.event?.longitude) {
            offerLatitude = offer.event.latitude;
            offerLongitude = offer.event.longitude;
          } else if (offer.latitude && offer.longitude) {
            offerLatitude = offer.latitude;
            offerLongitude = offer.longitude;
          } else {
            return false;
          }
          const offerLocation = {
            latitude: parseFloat(String(offerLatitude)),
            longitude: parseFloat(String(offerLongitude)),
          };
          const distance = convertFootToMiles(
            calculateDistance(targetLocation, offerLocation)
          );
          return distance <= 1.5;
        });
        const seenOfferIds = new Set();
        const uniqueOffers = filteredOffers.filter((offer) => {
          if (seenOfferIds.has(offer.id)) {
            return false;
          }
          seenOfferIds.add(offer.id);
          return true;
        });
        return uniqueOffers.map((offer) => ({
          ...offer,
          customCategory: 'offers',
        }));
      },
      loadEvents: () => {
        if (!isDataLoaded) return [];
        // Use accumulatedEvents for Comic-Con events and accumulatedNoBadgeEvents for no-badge events
        const comicConEvents = accumulatedEvents;
        const noBadgeEvents = accumulatedNoBadgeEvents;
        const comicConEventsWithTopics = comicConEvents.map((event) => ({
          ...event,
          topics: [{id: comicConTopicId, name: 'Comic-Con'}],
        }));
        const noBadgeEventsWithTopics = noBadgeEvents.map((event) => ({
          ...event,
          topics: [{id: noBadgeTopicId, name: 'No Badge Required'}],
        }));
        const eventMap = new Map();
        comicConEventsWithTopics.forEach((event) => {
          eventMap.set(event.id, event);
        });
        noBadgeEventsWithTopics.forEach((event) => {
          if (eventMap.has(event.id)) {
            const existingEvent = eventMap.get(event.id);
            const mergedTopics = [...(existingEvent.topics || [])];
            const hasNoBadgeTopic = mergedTopics.some((topic) => topic.id == noBadgeTopicId);
            if (!hasNoBadgeTopic) {
              mergedTopics.push({id: noBadgeTopicId, name: 'No Badge Required'});
            }
            const updatedEvent = {
              ...existingEvent,
              topics: mergedTopics,
            };
            eventMap.set(event.id, updatedEvent);
          } else {
            eventMap.set(event.id, event);
          }
        });
        const allEvents = Array.from(eventMap.values());
        const comicConStartDate = dayjs('2025-07-24').utc().startOf('day');
        const comicConEndDate = dayjs('2025-07-27').utc().endOf('day');
        const categorizedEvents = allEvents
          .filter((event) => {
            if (event.is_meetup === true) {
              return false;
            }
            const topicsArray = event.topics_of_interest || event.topics || [];
            const hasComicConTopic = topicsArray.some((topic) => topic.id == comicConTopicId);
            const hasNoBadgeTopic = topicsArray.some((topic) => topic.id == noBadgeTopicId);
            if (!hasComicConTopic && !hasNoBadgeTopic) {
              return false;
            }
            const eventStartDate = dayjs.utc(event.start_date_time || event.starts_at);
            const eventEndDate = dayjs.utc(
              event.end_date_time || event.ends_at || event.start_date_time || event.starts_at
            );
            const isWithinDateRange =
              eventStartDate.isBetween(comicConStartDate, comicConEndDate, null, '[]') ||
              eventEndDate.isBetween(comicConStartDate, comicConEndDate, null, '[]') ||
              (eventStartDate.isBefore(comicConStartDate) && eventEndDate.isAfter(comicConEndDate));
            if (!isWithinDateRange) {
              return false;
            }
            return true;
          })
          .map((event) => {
            const topicsArray = event.topics_of_interest || event.topics || [];
            const hasNoBadgeTopic = topicsArray.some((topic) => topic.id == noBadgeTopicId);
            return {
              ...event,
              customCategory: hasNoBadgeTopic ? 'no-badge-activations' : 'events',
              originalType: 'event',
            };
          });

        return categorizedEvents;
      },
      loadMeetups: () => {
        if (!isDataLoaded) return [];
        const comicConStartDate = dayjs('2025-07-25').utc().startOf('day');
        const comicConEndDate = dayjs('2025-07-27').utc().endOf('day');
        const filteredMeetups = accumulatedMeetups.filter((meetup) => {
          const meetupDate = dayjs.utc(meetup.start_date_time);
          const isWithinDateRange = meetupDate.isBetween(
            comicConStartDate,
            comicConEndDate,
            null,
            '[]'
          );
          if (!isWithinDateRange) return false;
          if (!meetup.latitude || !meetup.longitude) return false;
          const meetupLocation = {
            latitude: parseFloat(String(meetup.latitude)),
            longitude: parseFloat(String(meetup.longitude)),
          };
          const distance = convertFootToMiles(
            calculateDistance(targetLocation, meetupLocation)
          );
          return distance <= 1.5;
        });
        return filteredMeetups;
      },
      loadPlaces: () => {
        if (!isDataLoaded) return [];
        const allPlaces = fetchComicConPlaces.data?.pages.flatMap((page) => page.data.data) || [];
        const categorizedPlaces = allPlaces
          .filter((place) => {
            const topicsArray = place.topics_of_interest || place.topics || [];
            const hasComicConTopic = topicsArray.some((topic) => topic.id == comicConTopicId);
            const hasNoBadgeTopic = topicsArray.some((topic) => topic.id == noBadgeTopicId);
            return hasComicConTopic || hasNoBadgeTopic;
          })
          .map((place) => {
            const topicsArray = place.topics_of_interest || place.topics || [];
            const hasNoBadgeTopic = topicsArray.some((topic) => topic.id == noBadgeTopicId);
            return {
              ...place,
              customCategory: hasNoBadgeTopic ? 'no-badge-activations' : 'places',
              originalType: 'place',
            };
          });
        return categorizedPlaces;
      },
      loadPartners: () => [],
    };
  }, [
    // Only depend on the accumulated arrays and essential dependencies
    accumulatedOffers,
    accumulatedEvents,
    accumulatedMeetups,
    accumulatedNoBadgeEvents,
    isDataLoaded,
    eventOffers,
    comicConTopicId,
    noBadgeTopicId,
    event,
    targetLocation.latitude,
    targetLocation.longitude,
  ]);

  const comicConConfig = useMemo(
    () => ({
      title: 'Comic-Con San Diego',
      layout: 'map-and-list' as const,
      mapConfig: {
        enabled: true,
        title: 'Nearby Experiences Map',
        center: targetLocation,
        initialZoom: 14,
        height: '275px',
        showUserLocation: true,
      },

      sections: {
        meetups: {
          enabled: true,
          title: 'Meet-ups',
          icon: '👥',
          backgroundColor: '#f0f8ff',
          textColor: '#4169e1',
          borderColor: '#b0c4de',
          hoverColor: '#e0e6ff',
          circleColor: '#9C27B0',
          description: '{count} meet-up{count !== 1 ? "s" : ""} during Comic-Con',
          sortBy: 'distance' as const,
        },
        'no-badge-activations': {
          enabled: true,
          title: 'No Badge Required',
          icon: '🎭',
          backgroundColor: '#e6f3ff',
          textColor: '#0066cc',
          borderColor: '#99ccff',
          hoverColor: '#ccddff',
          circleColor: '#FF6B35',
          description: '{count} activation{count !== 1 ? "s" : ""} available without a badge',
          sortBy: 'distance' as const,
        },

        events: {
          enabled: true,
          title: 'Events',
          icon: '📅',
          backgroundColor: '#fff0f5',
          textColor: '#dc143c',
          borderColor: '#ffb6c1',
          hoverColor: '#ffe4e1',
          circleColor: '#E91E63',
          description: '{count} Comic-Con event{count !== 1 ? "s" : ""} happening',
          sortBy: 'distance' as const,
        },
        offers: {
          enabled: true,
          title: 'Offers',
          icon: '💰',
          backgroundColor: '#f0fff0',
          textColor: '#228b22',
          borderColor: '#90ee90',
          hoverColor: '#e0ffe0',
          circleColor: '#00BCD4',
          description: '{count} offer{count !== 1 ? "s" : ""} available during Comic-Con',
          sortBy: 'distance' as const,
        },
        places: {
          enabled: true,
          title: 'Places',
          icon: '📍',
          backgroundColor: '#fffaf0',
          textColor: '#ff8c00',
          borderColor: '#ffd700',
          hoverColor: '#fff8dc',
          circleColor: '#FFD700',
          description: '{count} Comic-Con related {count !== 1 ? "places" : "place"} to visit',
          sortBy: 'distance' as const,
        },
        partners: {
          enabled: false,
          title: 'Partners',
          icon: '🏢',
          backgroundColor: '#f0fff4',
          textColor: '#38a169',
          borderColor: '#9ae6b4',
          hoverColor: '#c6f6d5',
          circleColor: '#38a169',
          description: '{count} partner{count !== 1 ? "s" : ""}',
          sortBy: 'distance' as const,
        },
      },
      distanceFilter: {
        enabled: true,
        maxDistance: 1.5,
        centerLocation: targetLocation,
      },
      dateFilter: {
        enabled: true,
        referenceEvent: event,
        mode: 'date-range' as const,
      },
    }),
    [event]
  );

  return (
    <>
      {isInitialLoading && (
        <div
          style={{
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <SpinnerLoader />
        </div>
      )}
      {areParamsValid && !isInitialLoading && (
        <CuratedEventContent
          config={comicConConfig}
          customDataLoader={customDataLoader}
          event={event}
        />
      )}
    </>
  );
};

export default ComicConContent;
